import React, { useState, useEffect, memo } from "react";
import { Card, Space, DatePicker, Button, Spin, Skeleton, message, Segmented, Tag, Statistic, Row, Col } from "antd";
import { MetricCardHeader } from "./Shared/MetricCardHeader";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  MessageOutlined,
  CommentOutlined,
  LikeOutlined,
  SmileOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getEngagementFunnel, getEngagementFunnelTrend } from "../../api/api";
import type { EngagementFunnelResponse, EngagementFunnelTrendResponse } from "../../types/api-models";
import { 
  formatXAxisLabel, 
  formatTooltipLabel,
  getXAxisInterval
} from "../../utils/dateFormatters";

const { RangePicker } = DatePicker;

type AggregationType = 'daily' | 'weekly' | 'monthly';

interface FunnelProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const UserEngagementFunnel: React.FC<FunnelProps> = ({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EngagementFunnelResponse | null>(null);
  const [trendData, setTrendData] = useState<EngagementFunnelTrendResponse | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);


  useEffect(() => {
    // Skip initial fetch in expanded mode
    if (isExpanded) return;
    
    // For collapsed view, use dashboard data
    if (dashboardData?.engagement_funnel) {
      setData(dashboardData.engagement_funnel);
    }
  }, [isExpanded, dashboardData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let from_date: string;
      let to_date: string;
      
      // Calculate date range based on aggregation
      if (aggregation === 'daily') {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          setLoading(false);
          return;
        }
        from_date = dateRange[0].format('YYYY-MM-DD');
        to_date = dateRange[1].format('YYYY-MM-DD');
        
        // For daily with small range, use aggregate view
        const diffDays = dateRange[1].diff(dateRange[0], 'day');
        if (diffDays <= 30) {
          // Use original aggregate endpoint
          const response = await getEngagementFunnel(from_date, to_date);
          setData(response);
          setTrendData(null);
        } else {
          // Use trend endpoint for longer ranges
          const response = await getEngagementFunnelTrend(aggregation, from_date, to_date);
          setTrendData(response);
          setData(null);
        }
      } else {
        // Weekly/Monthly always use trend view
        const response = await getEngagementFunnelTrend(aggregation);
        setTrendData(response);
        setData(null);
      }
    } catch (error) {
      message.error('Failed to load engagement funnel');
      setData(null);
      setTrendData(null);
    } finally {
      setLoading(false);
    }
  };

  // Skeleton loader for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  // Show loading spinner
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading engagement funnel..." />
      </div>
    );
  }

  const hasData = data && data.all_sessions > 0;
  const hasTrendData = trendData && trendData.data_points && trendData.data_points.length > 0;
  
  let emptyStateMessage;
  if (!data && !trendData && isExpanded) {
    emptyStateMessage = (
      <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
        <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view engagement funnel</p>
        <p style={{ fontSize: 13, color: '#999' }}>Adjust filters above and click "Apply Filters" to view data</p>
      </div>
    );
  } else if (!data && !trendData) {
    emptyStateMessage = (
      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
        No data available
      </div>
    );
  }

  // If no data, show empty state
  if (!hasData && !hasTrendData) {
    if (!isExpanded) {
      return emptyStateMessage;
    }
    // In expanded mode, we'll show controls + empty state below
  }

  // Render trend icon based on direction
  const TrendIcon: React.FC<{ trend: 'improving' | 'declining' | 'stable' }> = ({ trend }) => {
    if (trend === 'improving') return <ArrowUpOutlined style={{ color: '#52c41a' }} />;
    if (trend === 'declining') return <ArrowDownOutlined style={{ color: '#ff4d4f' }} />;
    return <MinusOutlined style={{ color: '#999' }} />;
  };

  // TREND VIEW for Weekly/Monthly (Line Chart)
  const renderTrendView = () => {
    if (!hasTrendData || !trendData) return null;

    const { data_points, current_period, previous_period, trends, insights } = trendData;

    return (
      <div>
        {/* Comparison Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>Followup Rate</span>}
                value={current_period.with_followup_pct}
                precision={1}
                suffix="%"
                prefix={<TrendIcon trend={trends.followup_trend} />}
                valueStyle={{ 
                  fontSize: 24,
                  color: trends.followup_trend === 'improving' ? '#52c41a' : 
                         trends.followup_trend === 'declining' ? '#ff4d4f' : '#1890ff'
                }}
              />
              {previous_period && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {trends.followup_change_pct > 0 ? '+' : ''}{trends.followup_change_pct}% vs prev
                </div>
              )}
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>Feedback Rate</span>}
                value={current_period.with_feedback_pct}
                precision={1}
                suffix="%"
                prefix={<TrendIcon trend={trends.feedback_trend} />}
                valueStyle={{ 
                  fontSize: 24,
                  color: trends.feedback_trend === 'improving' ? '#52c41a' : 
                         trends.feedback_trend === 'declining' ? '#ff4d4f' : '#1890ff'
                }}
              />
              {previous_period && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {trends.feedback_change_pct > 0 ? '+' : ''}{trends.feedback_change_pct}% vs prev
                </div>
              )}
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>Positive Feedback</span>}
                value={current_period.with_positive_feedback_pct}
                precision={1}
                suffix="%"
                prefix={<TrendIcon trend={trends.positive_trend} />}
                valueStyle={{ 
                  fontSize: 24,
                  color: trends.positive_trend === 'improving' ? '#52c41a' : 
                         trends.positive_trend === 'declining' ? '#ff4d4f' : '#1890ff'
                }}
              />
              {previous_period && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {trends.positive_change_pct > 0 ? '+' : ''}{trends.positive_change_pct}% vs prev
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Trend Line Chart */}
        <div style={{ height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data_points}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period_start" 
              tickFormatter={(val) => formatXAxisLabel(val, aggregation)}
              interval={getXAxisInterval(data_points.length)}
              angle={data_points.length > 30 ? -45 : 0}
              textAnchor={data_points.length > 30 ? 'end' : 'middle'}
              height={data_points.length > 30 ? 60 : 30}
              style={{ fontSize: 11 }}
            />
            <YAxis 
              domain={[0, 100]}
              label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              style={{ fontSize: 11 }}
            />
            <Tooltip 
              labelFormatter={(val) => formatTooltipLabel(val, aggregation)}
              formatter={(value: number) => [`${value}%`, '']}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend 
              wrapperStyle={{ fontSize: 12 }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="with_followup_pct"
              name="Followup Rate"
              stroke="#8884d8"
              strokeWidth={2}
              dot={{ r: 4, fill: '#8884d8' }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="with_feedback_pct"
              name="Feedback Rate"
              stroke="#82ca9d"
              strokeWidth={2}
              dot={{ r: 4, fill: '#82ca9d' }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="with_positive_feedback_pct"
              name="Positive Feedback"
              stroke="#ffc658"
              strokeWidth={2}
              dot={{ r: 4, fill: '#ffc658' }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
        </div>

        {/* AI-Generated Insights */}
        {insights && insights.length > 0 && (
          <div style={{ 
            marginTop: 20,
            padding: '12px 16px',
            backgroundColor: '#f0f5ff',
            borderRadius: '6px',
            borderLeft: '4px solid #1890ff'
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
              📊 Key Insights
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#666' }}>
              {insights.map((insight, index) => (
                <li key={index} style={{ marginBottom: 4 }}>{insight}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // AGGREGATE VIEW for Daily (Horizontal Bar Chart)
  const renderAggregateView = () => {
    if (!hasData || !data) return null;

    // Fix: We don't have direct "dropoff rate" in the aggregate data readily available as a single number field
    // But we can infer it or simply show "Completion Rate" (Positive Feedback / All Sessions)?
    // The model has dropoff_trend. Let's assume we want to show Dropoff Rate or Engagement Rate.
    // Let's use "Engagement Rate" (Followup + Feedback / Sessions)? Or just rely on what the dropoff trend is tracking.
    // The backend `dropoff_trend` tracks dropoff at the first step (Sessions without followup).
    // So "Dropoff Rate" = (Sessions - SessionsWithFollowup) / Sessions.
    
    // Let's calculate Dropoff Rate for display value
    const dropoffRate = data.all_sessions > 0 
      ? ((data.all_sessions - data.with_followup) / data.all_sessions * 100) 
      : 0;

    // Prepare funnel data
    const funnelData = [
      {
        stage: "Asked Question",
        value: data.all_sessions,
        pct: 100,
        fill: "#8884d8",
        icon: <MessageOutlined />,
      },
      {
        stage: "Asked Follow-up",
        value: data.with_followup,
        pct: data.with_followup_pct,
        fill: "#83a6ed",
        icon: <CommentOutlined />,
      },
      {
        stage: "Gave Feedback",
        value: data.with_feedback,
        pct: data.with_feedback_pct,
        fill: "#8dd1e1",
        icon: <LikeOutlined />,
      },
      {
        stage: "Gave Positive Feedback",
        value: data.with_positive_feedback,
        pct: data.with_positive_feedback_pct,
        fill: "#82ca9d",
        icon: <SmileOutlined />,
      },
    ];

    const maxValue = funnelData[0].value;

    return (
      <div className="flex flex-col h-full">
         <div className="mb-4">
            <MetricCardHeader 
              title="Session Dropoff Rate"
              value={`${dropoffRate.toFixed(1)}%`}
              trend={dashboardData?.engagement_funnel?.dropoff_trend}
              subtitle="Sessions with no follow-up"
              inverseTrend={true} // Up is Bad for Dropoff
            />
         </div>

        {/* Horizontal Bar Chart */}
        <div style={{ minHeight: isExpanded ? 280 : 240 }}>
          <div style={{ height: isExpanded ? 280 : 240 }}>
            <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={funnelData} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
            >
              <XAxis type="number" hide />
              <YAxis 
                type="category" 
                dataKey="stage" 
                width={110}
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                formatter={(value: number) => [
                  `${value} (${((value / maxValue) * 100).toFixed(1)}%)`,
                  'Count'
                ]}
              />
              <Bar dataKey="value" isAnimationActive>
                {funnelData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          </div>
        </div>

        {/* KPI Row */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          marginTop: 20,
          paddingTop: 16,
          borderTop: '1px solid #f0f0f0'
        }}>
          {funnelData.map((item) => (
            <div key={item.stage} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, color: item.fill, marginBottom: 4 }}>
                {item.icon}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#222' }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const chartComponent = hasTrendData ? renderTrendView() : hasData ? renderAggregateView() : emptyStateMessage;

  // If not expanded, return chart only
  if (!isExpanded) {
    return chartComponent;
  }

  // Expanded view with controls
  return (
    <div>
      {/* Controls Row */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontSize: 13, color: '#666' }}>Aggregation:</span>
          <Segmented
            value={aggregation}
            onChange={(value) => setAggregation(value as AggregationType)}
            options={[
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' }
            ]}
          />
          {aggregation === 'daily' ? (
            <>
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Date Range:</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: 260 }}
                disabledDate={(current) => {
                  if (!current) return false;
                  return current.isAfter(dayjs().endOf('day'));
                }}
              />
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
              Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
            </span>
          )}
        </Space>
        
        <Space>
          <Button type="primary" onClick={fetchData} loading={loading}>
            Apply Filters
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={!hasData && !hasTrendData}>
            Refresh
          </Button>
        </Space>
      </Space>

      {/* Chart */}
      {chartComponent}
      
      {/* Info footer */}
      {hasData && data && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#999', textAlign: 'center' }}>
          Showing {data.all_sessions.toLocaleString()} total sessions • 
          Aggregation: {data.aggregation_used} • 
          Date range: {dayjs(data.date_range.from).format('MMM D, YYYY')} - {dayjs(data.date_range.to).format('MMM D, YYYY')}
        </div>
      )}
      {hasTrendData && trendData && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#999', textAlign: 'center' }}>
          Showing {trendData.data_points.length} {aggregation} periods • 
          Date range: {dayjs(trendData.date_range.from).format('MMM D, YYYY')} - {dayjs(trendData.date_range.to).format('MMM D, YYYY')}
        </div>
      )}
    </div>
  );
};

const MemoizedUserEngagementFunnel = memo(UserEngagementFunnel);
MemoizedUserEngagementFunnel.displayName = 'UserEngagementFunnel';

export default MemoizedUserEngagementFunnel;
